import React, { useEffect, useRef } from 'react';
import type { TelegramUser } from '@wbstats/shared';
import { MockTelegramLoginButton } from './MockTelegramLoginButton';

interface Props {
  botName: string;
  onAuth: (user: TelegramUser) => void;
  useMock?: boolean;
}

// Define a type for the window with our custom property
interface WindowWithTelegramAuth extends Window {
  onTelegramAuth?: (user: TelegramUser) => void;
}

export const TelegramLoginButton: React.FC<Props> = ({ botName, onAuth, useMock = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (useMock) return;

    // IMPORTANT: Set up the global callback BEFORE loading the script
    (window as WindowWithTelegramAuth).onTelegramAuth = (user) => {
      console.log('Telegram auth callback triggered with user:', user);
      onAuth(user);
    };

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.async = true;
    script.setAttribute('data-telegram-login', botName);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-auth-url', window.location.origin);
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');

    // Capture ref value for cleanup
    const container = containerRef.current;

    // Clear previous widgets if any
    if (container) {
      container.innerHTML = '';
      container.appendChild(script);
    }

    return () => {
      if (container) {
        container.innerHTML = '';
      }
      // Clean up the global callback on unmount
      delete (window as WindowWithTelegramAuth).onTelegramAuth;
    };
  }, [botName, onAuth, useMock]);

  if (useMock) {
    return <MockTelegramLoginButton botName={botName} onAuth={onAuth} />;
  }

  return <div ref={containerRef} />;
};
